import { useCallback, useEffect, useRef, useState } from 'react';
import { DELAI_SAISIE_DIFFEREE_MS } from '@rh/shared';
import * as api from '../api';
import { useFile } from './contexte-file';
import { maintenantServeur } from './horloge';
import { ID_ONGLET } from './identifiants';
import { FORMAT_SAISIES, type RefContact, type SaisieLocale } from './types';

/**
 * Brouillon du panneau de contact, écrit sur le téléphone à la frappe
 * (docs/UI.md § Écran 3, mode hors ligne) : un rechargement ou un onglet tué
 * ne perd pas la saisie. Rien n'est écrit tant que l'utilisateur n'a rien
 * touché ; le pré-remplissage (charte signée) ne crée pas de brouillon.
 *
 * État initial, par ordre de priorité : le brouillon du compte, sinon la
 * saisie en file si elle est plus récente, sinon le contact du serveur.
 */

const ATTENTE_ECRITURE_MS = 400;
/** Deux onglets sur le même contact : un avertissement au plus toutes les 5 s. */
const INTERVALLE_DIFFUSION_MS = 5_000;

type Maj<S> = Partial<S> | ((saisie: S) => S);

export interface ParametresBrouillon<S extends SaisieLocale> {
  utilisateurId: string | null;
  contact: RefContact | null;
  modifiable: boolean;
  initiale: () => S;
}

export interface EtatBrouillon<S extends SaisieLocale> {
  saisie: S;
  /** Brouillon et saisie en file lus : le formulaire peut s'afficher. */
  pret: boolean;
  revision: number;
  /** Geste de l'utilisateur : écrit sur le téléphone après 400 ms. */
  modifier(maj: Maj<S>): void;
  /** Pré-remplissage : sans brouillon, et après la lecture locale s'il arrive avant elle. */
  initialiser(maj: Maj<S>): void;
  /** Remplace la saisie affichée sans rien écrire (saisie refusée, barème relu). */
  remplacer(saisie: S): void;
  /** Efface le brouillon et repart du contact du serveur. */
  vider(): Promise<void>;
  /** Écrit tout de suite ce qui ne l'est pas (avant une mise en file). */
  ecrireMaintenant(): Promise<void>;
  /** La révision donnée vient de partir en file. */
  noterMiseEnFile(revision: number): void;
  /** Des modifications gardées sur le téléphone ne sont pas encore en file. */
  nonEnregistre: boolean;
  restaure: { modifieLe: number; source: 'brouillon' | 'envoi' } | null;
  autreOnglet: boolean;
}

export function useBrouillon<S extends SaisieLocale>({
  utilisateurId,
  contact,
  modifiable,
  initiale,
}: ParametresBrouillon<S>): EtatBrouillon<S> {
  const { stockage, diffusion } = useFile();
  const [saisie, setSaisie] = useState<S>(initiale);
  const [pret, setPret] = useState(false);
  const [revision, setRevision] = useState(0);
  const [revisionMiseEnFile, setRevisionMiseEnFile] = useState<number | null>(null);
  const [restaure, setRestaure] = useState<EtatBrouillon<S>['restaure']>(null);
  const [autreOnglet, setAutreOnglet] = useState(false);

  // Les écritures partent de minuteurs et du démontage : elles lisent l'état par des références.
  const saisieRef = useRef(saisie);
  const revisionRef = useRef(0);
  const ecriteRef = useRef(0);
  const miseEnFileRef = useRef<number | null>(null);
  const creeLeRef = useRef<number | null>(null);
  const pretRef = useRef(false);
  const attenteRef = useRef<Array<Maj<S>>>([]);
  const minuteurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chaineRef = useRef<Promise<void>>(Promise.resolve());
  const publieLeRef = useRef(0);
  const initialeRef = useRef(initiale);
  initialeRef.current = initiale;
  const modifiableRef = useRef(modifiable);
  modifiableRef.current = modifiable;
  const contexteRef = useRef({ utilisateurId, contact, stockage });
  contexteRef.current = { utilisateurId, contact, stockage };

  const appliquer = useCallback((maj: Maj<S>, base: S): S => {
    return typeof maj === 'function' ? maj(base) : { ...base, ...maj };
  }, []);

  const afficher = useCallback((suivante: S) => {
    saisieRef.current = suivante;
    setSaisie(suivante);
  }, []);

  const annulerMinuteur = useCallback(() => {
    if (minuteurRef.current !== null) clearTimeout(minuteurRef.current);
    minuteurRef.current = null;
  }, []);

  /** Écritures en série : la dernière révision l'emporte toujours sur le disque. */
  const ecrireMaintenant = useCallback((): Promise<void> => {
    annulerMinuteur();
    const suite = chaineRef.current.then(async () => {
      const { utilisateurId: compte, contact: ref, stockage: s } = contexteRef.current;
      const aEcrire = revisionRef.current;
      if (!compte || !ref || !s || aEcrire <= ecriteRef.current || !modifiableRef.current) return;
      // Autre compte, déconnexion volontaire ou compte désactivé : plus rien n'entre sur le
      // téléphone. Une session seulement refusée (révoquée, expirée) garde la frappe en cours.
      if (!api.brouillonPermis(compte)) return;
      const maintenant = maintenantServeur();
      const creeLe = creeLeRef.current ?? maintenant;
      creeLeRef.current = creeLe;
      try {
        await s.ecrireBrouillon({
          format: FORMAT_SAISIES,
          utilisateurId: compte,
          contactId: ref.id,
          candidatureId: ref.candidatureId,
          type: ref.type,
          debuteLe: ref.debuteLe,
          saisie: saisieRef.current,
          revision: aEcrire,
          revisionMiseEnFile: miseEnFileRef.current,
          onglet: ID_ONGLET,
          creeLe,
          modifieLe: maintenant,
          expireLe: creeLe + DELAI_SAISIE_DIFFEREE_MS,
        });
      } catch (erreur) {
        console.error(erreur);
        return;
      }
      ecriteRef.current = Math.max(ecriteRef.current, aEcrire);
      if (maintenant - publieLeRef.current >= INTERVALLE_DIFFUSION_MS) {
        publieLeRef.current = maintenant;
        diffusion.publier({ type: 'brouillon-ecrit', utilisateurId: compte, contactId: ref.id, onglet: ID_ONGLET });
      }
    });
    chaineRef.current = suite;
    return suite;
  }, [annulerMinuteur, diffusion]);
  const ecrireRef = useRef(ecrireMaintenant);
  ecrireRef.current = ecrireMaintenant;

  // Lecture locale à l'ouverture du contact (et changement de compte).
  const contactId = contact?.id ?? null;
  const typeContact = contact?.type ?? null;
  useEffect(() => {
    if (!utilisateurId || !contactId || !typeContact || !stockage) return;
    let vivant = true;
    pretRef.current = false;
    setPret(false);
    const cle = [utilisateurId, contactId] as const;
    void Promise.all([
      stockage.lireBrouillon(cle).catch(() => undefined),
      stockage.lire(cle).catch(() => undefined),
    ]).then(([brouillon, envoi]) => {
      if (!vivant) return;
      const brouillonValable = brouillon?.saisie.type === typeContact ? brouillon : undefined;
      const envoiValable = envoi?.saisie?.type === typeContact ? envoi : undefined;
      let base: S;
      let source: EtatBrouillon<S>['restaure'] = null;
      if (brouillonValable && (!envoiValable || brouillonValable.modifieLe >= envoiValable.modifieLe)) {
        base = brouillonValable.saisie as S;
        source = { modifieLe: brouillonValable.modifieLe, source: 'brouillon' };
        revisionRef.current = brouillonValable.revision;
        miseEnFileRef.current = brouillonValable.revisionMiseEnFile;
      } else if (envoiValable?.saisie) {
        base = envoiValable.saisie as S;
        source = { modifieLe: envoiValable.modifieLe, source: 'envoi' };
        revisionRef.current = brouillon?.revision ?? 0;
        miseEnFileRef.current = revisionRef.current;
      } else {
        base = initialeRef.current();
        revisionRef.current = brouillon?.revision ?? 0;
        miseEnFileRef.current = brouillon ? brouillon.revisionMiseEnFile : null;
      }
      ecriteRef.current = revisionRef.current;
      creeLeRef.current = brouillon?.creeLe ?? envoi?.creeLe ?? null;
      for (const maj of attenteRef.current) base = appliquer(maj, base);
      attenteRef.current = [];
      afficher(base);
      setRevision(revisionRef.current);
      setRevisionMiseEnFile(miseEnFileRef.current);
      setRestaure(source);
      pretRef.current = true;
      setPret(true);
    });
    return () => {
      vivant = false;
      // Démontage ou autre contact : ce qui n'est pas écrit l'est tout de suite.
      void ecrireRef.current();
    };
  }, [utilisateurId, contactId, typeContact, stockage, afficher, appliquer]);

  // Page masquée ou quittée : écriture immédiate (Android tue volontiers l'onglet en arrière-plan).
  useEffect(() => {
    const surMasquage = () => {
      if (document.visibilityState === 'hidden') void ecrireRef.current();
    };
    const surDepart = () => void ecrireRef.current();
    document.addEventListener('visibilitychange', surMasquage);
    window.addEventListener('pagehide', surDepart);
    return () => {
      document.removeEventListener('visibilitychange', surMasquage);
      window.removeEventListener('pagehide', surDepart);
    };
  }, []);

  // Le même contact ouvert dans un autre onglet : dernière saisie gagnante, simple avertissement.
  useEffect(() => {
    setAutreOnglet(false);
    if (!utilisateurId || !contactId) return;
    return diffusion.ecouter((message) => {
      if (
        message.type === 'brouillon-ecrit' &&
        message.utilisateurId === utilisateurId &&
        message.contactId === contactId &&
        message.onglet !== ID_ONGLET
      ) {
        setAutreOnglet(true);
      }
    });
  }, [diffusion, utilisateurId, contactId]);

  useEffect(() => annulerMinuteur, [annulerMinuteur]);

  const modifier = useCallback(
    (maj: Maj<S>) => {
      if (!modifiableRef.current) return;
      afficher(appliquer(maj, saisieRef.current));
      revisionRef.current += 1;
      setRevision(revisionRef.current);
      annulerMinuteur();
      minuteurRef.current = setTimeout(() => void ecrireRef.current(), ATTENTE_ECRITURE_MS);
    },
    [afficher, appliquer, annulerMinuteur],
  );

  const initialiser = useCallback(
    (maj: Maj<S>) => {
      if (!pretRef.current) {
        attenteRef.current.push(maj);
        return;
      }
      afficher(appliquer(maj, saisieRef.current));
    },
    [afficher, appliquer],
  );

  const remplacer = useCallback((suivante: S) => afficher(suivante), [afficher]);

  const vider = useCallback(async () => {
    annulerMinuteur();
    await chaineRef.current;
    const { utilisateurId: compte, contact: ref, stockage: s } = contexteRef.current;
    if (compte && ref && s) await s.supprimerBrouillon([compte, ref.id]).catch((e: unknown) => console.error(e));
    revisionRef.current = 0;
    ecriteRef.current = 0;
    miseEnFileRef.current = null;
    creeLeRef.current = null;
    afficher(initialeRef.current());
    setRevision(0);
    setRevisionMiseEnFile(null);
    setRestaure(null);
  }, [afficher, annulerMinuteur]);

  const noterMiseEnFile = useCallback((revisionEnFile: number) => {
    miseEnFileRef.current = revisionEnFile;
    setRevisionMiseEnFile(revisionEnFile);
  }, []);

  return {
    saisie,
    pret,
    revision,
    modifier,
    initialiser,
    remplacer,
    vider,
    ecrireMaintenant,
    noterMiseEnFile,
    nonEnregistre: revision > (revisionMiseEnFile ?? 0),
    restaure,
    autreOnglet,
  };
}
